import { CommonModule } from '@angular/common';
import { AfterContentInit, ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatCardModule } from "@angular/material/card";
import
{
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef,
} from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { Store } from "@ngxs/store";
import { Observable, timer } from "rxjs";
import { EditTextDialogData } from "../../../interfaces";
import { PermissionService } from "../../../services";
import
{
    AddTranslationRequestModel,
    Language,
    UpdateNoteRequestModel,
    UpdateProductRequestModel,
    UpdateVersionRequestModel,
} from "../../../services/api";
import { AppState } from "../../../state";
import { ClearEdited, PostTranslation, ProductApiStatusState, PutNote, PutProduct, PutText, PutVersion } from "../../product/state";
import { ConfirmDialogComponent } from "../confirm-dialog/confirm-dialog.component";
import { SubscriptionHandlerComponent } from "../subscription-handler/subscription-handler.component";

@Component({
    selector: 'app-view-text-dialog',
    imports: [CommonModule, MatButtonModule, MatIconModule, MatFormFieldModule, MatInputModule, FormsModule, MatCardModule, ReactiveFormsModule],
    templateUrl: './view-text-dialog.component.html',
    styleUrl: './view-text-dialog.component.css',
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ViewTextDialogComponent extends SubscriptionHandlerComponent implements AfterContentInit
{

    editedValue$: Observable<boolean | null> = this.store.select(ProductApiStatusState.editedValue);

    languages$: Observable<Language[]> = this.store.select(AppState.languages);

    // Developers-text
    developersText: string;

    // Array of Translations
    localTranslations: string[] = [];
    // Array of Translation-Language-Ids
    localTranslationsLang: number[] = [];

    // Initial value of every Translation (used to check if the value has been edited)
    initialLocalTranslations: string[] = [];
    // Initial value of Developers text (used to check if the value has been edited)
    initialLocalDevelopersText: string = "";

    // Array of booleans to check if a specific Translation change was saved
    editPressedValues: boolean[] = [];
    // Boolean to check any Translation change was saved
    editPressedValue: boolean = false;
    // Boolean to check all Translation changes were saved
    editPressedAll: boolean = false;

    triggerReload: boolean = false;

    constructor(@Inject(MAT_DIALOG_DATA) public data: EditTextDialogData,
        public permissionService: PermissionService,
        private dialogRef: MatDialogRef<ViewTextDialogComponent>,
        private dialog: MatDialog,
        private store: Store)
    {
        super();
        this.developersText = data.value;
    }

    ngAfterContentInit()
    {
        this.subscribeTo(this.languages$, lan =>
        {
            if (lan !== null && lan !== undefined && lan.length > 0)
            {
                if (lan.length > 0)
                {
                    for (let i = 0; i < lan.length; i++)
                    {
                        // Initialize Translation-Arrays for every Language
                        this.localTranslations[i] = ""
                        this.localTranslationsLang[i] = lan[i].id!
                        this.editPressedValues[lan[i].id!] = false;
                    }
                    // Write the actual Array of Translations depending on the property
                    if (this.data.property === "Product")
                    {
                        do
                        {
                            if (this.data.product !== undefined)
                            {
                                for (let i = 0; i < this.data.product.description?.translations?.length!; i++)
                                {
                                    let index = this.localTranslationsLang.findIndex(x => x === this.data.product.description?.translations![i].language!.id);
                                    this.localTranslations[index] = this.data.product!.description?.translations![i].translatedText!
                                }
                            }
                        }
                        while (this.data.product === undefined)
                    } else if (this.data.property === "Version")
                    {
                        do
                        {
                            if (this.data.version !== undefined)
                            {
                                if (this.data.field === "Ban Reason")
                                {
                                    for (let i = 0; i < this.data.version.ban?.banReason?.translations?.length!; i++)
                                    {
                                        let index = this.localTranslationsLang.findIndex(x => x === this.data.version.ban?.banReason?.translations![i].language!.id);
                                        this.localTranslations[index] = this.data.version!.ban?.banReason?.translations![i].translatedText!
                                    }
                                } else if (this.data.field === "Description")
                                {
                                    for (let i = 0; i < this.data.version.description?.translations?.length!; i++)
                                    {
                                        let index = this.localTranslationsLang.findIndex(x => x === this.data.version.description?.translations![i].language!.id);
                                        this.localTranslations[index] = this.data.version!.description?.translations![i].translatedText!
                                    }
                                }
                            }
                        }
                        while (this.data.version === undefined)
                    } else if (this.data.property === "Note")
                    {
                        do
                        {
                            if (this.data.note !== undefined)
                            {
                                for (let i = 0; i < this.data.note.text?.translations?.length!; i++)
                                {
                                    let index = this.localTranslationsLang.findIndex(x => x === this.data.note.text?.translations![i].language!.id);
                                    this.localTranslations[index] = this.data.note!.text?.translations![i].translatedText!
                                }
                            }
                        }
                        while (this.data.note === undefined)
                    }
                    // Set initial values for Translations
                    this.initialLocalTranslations = structuredClone(this.localTranslations);
                    this.initialLocalDevelopersText = this.data.value;
                }
            }
        });
    }

    /**
     * Checks if any changes have been made to the developers text or translations.
     *
     * @returns {boolean} - Returns true if the developers text or any translation has been modified from their initial values, otherwise false.
     */
    allEnabled(): boolean
    {
        if (this.developersText !== this.initialLocalDevelopersText)
            return true;
        for (let i = 0; i < this.localTranslations.length; i++)
        {
            if (this.localTranslations[i] !== this.initialLocalTranslations[i])
            {
                return true;
            }
        }
        return false;
    }

    // Edit Translation of a specified Language
    editValue(languageId: number)
    {
        if (languageId == 1)
        {
            // If the Developer text is being edited
            // Confirm Dialog
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                data: {
                    title: "Edit Text",
                    message: "Editing texts might require new translations to be made. Are you sure you want to proceed?",
                    confirmButtonText: "Edit",
                    cancelButtonText: "Cancel"
                },
                disableClose: true,
                width: 'auto',
                height: 'auto'
            });

            dialogRef.afterClosed().subscribe(result =>
            {
                if (result)
                {
                    // If the user confirmed the edit, set the edited booleans
                    this.editPressedValues[languageId] = true;
                    this.editPressedValue = true;
                    // Update the Developer text
                    this.updateDeveloperText();
                    // Update the initial value of the Developer text
                    this.updateInitialLocalDevelopersText();
                } else
                {
                    // If the user didn't confirm the edit, set the specific edited boolean to false
                    this.editPressedValues[languageId] = false;
                    // If no translation is being edited, set the general edited boolean to false
                    if (Object.values(this.editPressedValues).every(value => !value))
                    {
                        this.editPressedValue = false;
                    }
                }
            });
        } else
        {
            // If any Translation is being edited
            // Confirm Dialog
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                data: {
                    title: "Edit Translation",
                    message: "Are you sure you want to edit this translation?",
                    confirmButtonText: "Edit",
                    cancelButtonText: "Cancel"
                },
                disableClose: true,
                width: 'auto',
                height: 'auto'
            });

            dialogRef.afterClosed().subscribe(result =>
            {
                if (result)
                {
                    // If the user confirmed the edit, set the edited booleans
                    this.editPressedValues[languageId] = true;
                    this.editPressedValue = true;
                    // Update the Translation
                    this.updateTranslation(languageId);
                } else
                {
                    // If the user didn't confirm the edit, set the specific edited boolean to false
                    this.editPressedValues[languageId] = false;
                    // If no translation is being edited, set the general edited boolean to false
                    if (Object.values(this.editPressedValues).every(value => !value))
                    {
                        this.editPressedValue = false;
                    }
                }

            });
        }
    }

    updateDeveloperText()
    {
        // If-Statement for editing every possible value
        if (this.data.property === "Product")
        {
            if (this.data.field === "Description")
            {
                let updatedProduct: UpdateProductRequestModel = {
                    id: this.data.id,
                    description: this.developersText,
                }
                this.store.dispatch(new PutProduct(updatedProduct))
            }
        } else if (this.data.property === "Version")
        {
            if (this.data.field === "Description")
            {
                let updatedVersion: UpdateVersionRequestModel = {
                    id: this.data.id,
                    description: this.developersText,
                }
                this.store.dispatch(new PutVersion(updatedVersion))
            } else if (this.data.field === "Ban Reason")
            {
                let updatedVersion: UpdateVersionRequestModel = {
                    id: this.data.id,
                    banReason: this.developersText,
                }
                this.store.dispatch(new PutVersion(updatedVersion))
            }
        } else if (this.data.property === "Note")
        {
            if (this.data.field === "Text")
            {
                let updatedNote: UpdateNoteRequestModel = {
                    id: this.data.id,
                    text: this.developersText,
                }
                this.store.dispatch(new PutNote(updatedNote))
            }
        }
        // Set triggerReload so the parent component knows that the data has been changed
        this.triggerReload = true;
    }

    updateTranslation(languageId: number)
    {
        let newTranslation = true;
        let index = -1;
        // If-Statement for editing every possible value
        if (this.data.property === "Product")
        {
            if (this.data.field === "Description")
            {
                let desc = structuredClone(this.data.product.description!);
                // Find the Translation for the selected Language
                for (let i = 0; i < desc.translations?.length!; i++)
                {
                    if (desc.translations![i].language!.id === languageId)
                    {
                        // If Translation already exists, update it
                        index = this.localTranslationsLang.findIndex(x => x === languageId);
                        desc.translations![i].translatedText = this.localTranslations[index];
                        this.store.dispatch(new PutText(desc));
                        newTranslation = false;
                        break;
                    }
                }
                if (newTranslation)
                {
                    // If Translation doesn't exist, create a new one
                    index = this.localTranslationsLang.findIndex(x => x === languageId);
                    let translation: AddTranslationRequestModel = {
                        textId: desc.id,
                        languageId: languageId,
                        translatedText: this.localTranslations[index],
                    }
                    this.store.dispatch(new PostTranslation(translation))
                }
            }
        } else if (this.data.property === "Version")
        {
            if (this.data.field === "Description")
            {
                let desc = structuredClone(this.data.version.description!);
                // Find the Translation for the selected Language
                for (let i = 0; i < desc.translations?.length!; i++)
                {
                    if (desc.translations![i].language!.id === languageId)
                    {
                        // If Translation already exists, update it
                        index = this.localTranslationsLang.findIndex(x => x === languageId);
                        desc.translations![i].translatedText = this.localTranslations[index];
                        this.store.dispatch(new PutText(desc));
                        newTranslation = false;
                        break;
                    }
                }
                if (newTranslation)
                {
                    // If Translation doesn't exist, create a new one
                    index = this.localTranslationsLang.findIndex(x => x === languageId);
                    let translation: AddTranslationRequestModel = {
                        textId: desc.id,
                        languageId: languageId,
                        translatedText: this.localTranslations[index],
                    }
                    this.store.dispatch(new PostTranslation(translation))
                }
            } else if (this.data.field === "Ban Reason")
            {
                let banReason = structuredClone(this.data.version.ban?.banReason!);
                // Find the Translation for the selected Language
                for (let i = 0; i < banReason.translations?.length!; i++)
                {
                    if (banReason.translations![i].language!.id === languageId)
                    {
                        // If Translation already exists, update it
                        index = this.localTranslationsLang.findIndex(x => x === languageId);
                        banReason.translations![i].translatedText = this.localTranslations[index];
                        this.store.dispatch(new PutText(banReason));
                        newTranslation = false;
                        break;
                    }
                }
                if (newTranslation)
                {
                    // If Translation doesn't exist, create a new one
                    index = this.localTranslationsLang.findIndex(x => x === languageId);
                    let translation: AddTranslationRequestModel = {
                        textId: banReason.id,
                        languageId: languageId,
                        translatedText: this.localTranslations[index],
                    }
                    this.store.dispatch(new PostTranslation(translation))
                }
            }
        } else if (this.data.property === "Note")
        {
            if (this.data.field === "Text")
            {
                let desc = structuredClone(this.data.note.text!);
                // Find the Translation for the selected Language
                for (let i = 0; i < desc.translations?.length!; i++)
                {
                    if (desc.translations![i].language!.id === languageId)
                    {
                        // If Translation already exists, update it
                        index = this.localTranslationsLang.findIndex(x => x === languageId);
                        desc.translations![i].translatedText = this.localTranslations[index];
                        this.store.dispatch(new PutText(desc));
                        newTranslation = false;
                        break;
                    }
                }
                if (newTranslation)
                {
                    // If Translation doesn't exist, create a new one
                    index = this.localTranslationsLang.findIndex(x => x === languageId);
                    let translation: AddTranslationRequestModel = {
                        textId: desc.id,
                        languageId: languageId,
                        translatedText: this.localTranslations[index],
                    }
                    this.store.dispatch(new PostTranslation(translation))
                }
            }
        }
        // Set triggerReload so the parent component knows that the data has been changed
        this.triggerReload = true;
        // Update the initial value of the Translation
        this.updateInitialLocalTranslations(index);
    }

    updateInitialLocalTranslations(index: number)
    {
        // Make Clone of the initial Translation-Array and update the specific Translation
        let value = structuredClone(this.initialLocalTranslations);
        value[index] = this.localTranslations[index];
        this.initialLocalTranslations = value;
    }

    updateInitialLocalDevelopersText()
    {
        this.initialLocalDevelopersText = this.developersText;
    }

    editAllValues()
    {
        const dialogRef = this.dialog.open(ConfirmDialogComponent, {
            data: {
                title: "Edit All Text Values",
                message: "Are you sure you want to edit all text values?",
                confirmButtonText: "Edit",
                cancelButtonText: "Cancel"
            },
            disableClose: true,
            width: 'auto',
            height: 'auto'
        });

        dialogRef.afterClosed().subscribe(result =>
        {
            if (result)
            {
                // If the user confirmed the edit, set the edited all boolean
                this.editPressedAll = true;
                // Update the Developer text if it has been edited
                if (this.developersText !== this.data.value)
                {
                    this.updateDeveloperText();
                }
                // Update the Translations if they have been edited
                for (let i = 0; i < this.localTranslations.length; i++)
                {
                    if (this.localTranslations[i] !== this.initialLocalTranslations[i])
                    {
                        this.updateTranslation(this.localTranslationsLang[i]);
                    }
                }
                // Close the dialog 2 seconds after editing
                this.subscribeTo(this.editedValue$, x =>
                {
                    if (x) timer(2000).subscribe(() => this.dialogRef.close(true))
                })
            } else
            {
                this.editPressedAll = false;
            }
        });

    }

    closeDialog()
    {
        if (this.allEnabled())
        {
            // If modification is not saved, confirm closing the dialog
            const dialogRef = this.dialog.open(ConfirmDialogComponent, {
                data: {
                    title: "Unsaved Changes",
                    message: "You have unsaved changes that will be discarded. Are you sure you want to close?",
                    confirmButtonText: "Close",
                    cancelButtonText: "Continue Editing"
                },
                disableClose: true,
                width: 'auto',
                height: 'auto'
            });

            // If the user confirmed the closing, close the dialog
            dialogRef.afterClosed().subscribe(result =>
            {
                // triggerReload is set to true if the data has been changed
                result && this.dialogRef.close(this.triggerReload);
            });
        } else
            // triggerReload is set to true if the data has been changed
            this.dialogRef.close(this.triggerReload);
    }

    onTextareaChange(languageId: number)
    {
        // Update Translation changed status on textarea change
        this.editPressedValues[languageId] = false;
        this.editPressedValue = false;
        // Clear Edited status
        this.store.dispatch(new ClearEdited());
    }
}
